// The appearance preference — System / Light / Dark — and the one fact the CSS
// reads: the RESOLVED theme on `<html data-theme="light|dark">`. The DOM never
// holds "system"; System is resolved here against the OS media query.
//
// Pure, no React. `public/theme-boot.js` is a hand-kept ES5 mirror of
// `THEME_STORAGE_KEY`, `DARK_MEDIA_QUERY` and `resolveTheme` that sets the
// attribute before first paint; `test/theme-boot.test.ts` pins the two together.
//
// Every storage and media-query access is guarded and wrapped: a private
// window, blocked site data or a jsdom without `matchMedia` must render the
// page (as System / light), never throw.

export const THEME_STORAGE_KEY = "loom.ui.theme";
export const DARK_MEDIA_QUERY = "(prefers-color-scheme: dark)";

export const THEME_PREFERENCES = ["system", "light", "dark"] as const;
export type ThemePreference = (typeof THEME_PREFERENCES)[number];
export type ResolvedTheme = "light" | "dark";

const noop = (): void => {};

export function isThemePreference(value: unknown): value is ThemePreference {
  return (
    typeof value === "string" &&
    (THEME_PREFERENCES as readonly string[]).includes(value)
  );
}

/** The stored choice. Only "light" and "dark" are ever stored — an absent key,
 *  any other value, or storage that cannot be read all mean System. */
export function readThemePreference(): ThemePreference {
  if (typeof window === "undefined") return "system";
  try {
    const storage = window.localStorage;
    if (!storage) return "system";
    const value = storage.getItem(THEME_STORAGE_KEY);
    return value === "light" || value === "dark" ? value : "system";
  } catch {
    return "system";
  }
}

/** Persist a choice. System removes the key (a missing key IS System), so the
 *  stored vocabulary stays two words. Storage errors are swallowed: the caller
 *  keeps the choice in memory. */
export function writeThemePreference(pref: ThemePreference): void {
  if (typeof window === "undefined") return;
  try {
    const storage = window.localStorage;
    if (!storage) return;
    if (pref === "light" || pref === "dark") {
      storage.setItem(THEME_STORAGE_KEY, pref);
    } else {
      storage.removeItem(THEME_STORAGE_KEY);
    }
  } catch {
    /* storage unavailable — the in-memory choice stands */
  }
}

/**
 * Where "System" reads its answer when it is not the OS: the Focus panel
 * (`src/panel/`) feeds its host's `hostContext.theme` here, so a panel inside
 * a dark assistant is dark. Set by the panel's entry before render; the web
 * never sets it, and System follows the OS media query.
 */
export interface SystemThemeSource {
  readonly prefersDark: () => boolean;
  readonly subscribe: (onChange: () => void) => () => void;
}

let systemThemeSource: SystemThemeSource | null = null;

export function setSystemThemeSource(source: SystemThemeSource | null): void {
  systemThemeSource = source;
}

/** Whether the OS asks for dark. False without `matchMedia` or when it throws. */
export function systemPrefersDark(): boolean {
  if (systemThemeSource !== null) return systemThemeSource.prefersDark();
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  try {
    return window.matchMedia(DARK_MEDIA_QUERY).matches === true;
  } catch {
    return false;
  }
}

/** Subscribe to OS theme changes (the `useSyncExternalStore` subscribe leg).
 *  Returns a no-op unsubscribe where there is nothing to listen to. */
export function subscribeSystemTheme(onChange: () => void): () => void {
  if (systemThemeSource !== null) return systemThemeSource.subscribe(onChange);
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return noop;
  }
  let mql: MediaQueryList;
  try {
    mql = window.matchMedia(DARK_MEDIA_QUERY);
    if (!mql || typeof mql.addEventListener !== "function") return noop;
    mql.addEventListener("change", onChange);
  } catch {
    return noop;
  }
  return () => {
    try {
      mql.removeEventListener("change", onChange);
    } catch {
      /* the list is gone — nothing left to detach */
    }
  };
}

/** A pinned choice wins; System follows the OS. */
export function resolveTheme(
  pref: ThemePreference,
  systemDark: boolean,
): ResolvedTheme {
  if (pref === "dark") return "dark";
  if (pref === "light") return "light";
  return systemDark ? "dark" : "light";
}

/** Write the resolved theme where the CSS reads it. `colorScheme` rides along
 *  so the canvas, scrollbars and native controls follow before any stylesheet
 *  applies (tokens.css repeats it as a backstop). */
export function applyResolvedTheme(
  theme: ResolvedTheme,
  root: HTMLElement = document.documentElement,
): void {
  root.dataset.theme = theme;
  root.style.colorScheme = theme;
}
